import { lazy } from "react";
import { PRODUCTS } from "./products";

declare const process: { env: Record<string, string | undefined> };

const sfOverview = () => import("./steps/shipping-fulfillment/overview");
const sfCurrentSoftware = () =>
  import("./steps/shipping-fulfillment/current-software");
const sfChannels = () => import("./steps/shipping-fulfillment/channels");
const sfCarriers = () => import("./steps/shipping-fulfillment/carriers");
const sfPackaging = () => import("./steps/shipping-fulfillment/packaging");
const sfPackingSlip = () => import("./steps/shipping-fulfillment/packing-slip");
const sfShippingAutomations = () =>
  import("./steps/shipping-fulfillment/shipping-automations");
const sfOrderAutomations = () =>
  import("./steps/shipping-fulfillment/order-automations");
const sfInternational = () =>
  import("./steps/shipping-fulfillment/international");
const sfViews = () => import("./steps/shipping-fulfillment/views");
const sfBilling = () => import("./steps/shipping-fulfillment/billing");
const sfPainPoints = () => import("./steps/shipping-fulfillment/pain-points");
const sfCompletion = () => import("./steps/shipping-fulfillment/completion");

const cbOverview = () => import("./steps/chargebacks/overview");
const cbHowItWorks = () => import("./steps/chargebacks/how-it-works");
const cbPermissions = () => import("./steps/chargebacks/permissions");
const cbDisputeSources = () => import("./steps/chargebacks/dispute-sources");
const cbBilling = () => import("./steps/chargebacks/billing");
const cbGoLive = () => import("./steps/chargebacks/go-live");
const cbCompletion = () => import("./steps/chargebacks/completion");

const oeOverview = () => import("./steps/order-editing/overview");
const oeVideoIntro = () => import("./steps/order-editing/video-intro");
const oeEnabledActions = () => import("./steps/order-editing/enabled-actions");
const oeTimeWindow = () => import("./steps/order-editing/time-window");
const oePlatforms = () => import("./steps/order-editing/platforms");
const oeWidgetPreview = () => import("./steps/order-editing/widget-preview");
const oeAppEmbed = () => import("./steps/order-editing/app-embed");
const oeCompletion = () => import("./steps/order-editing/completion");

const ctxOverview = () => import("./steps/contextual/overview");
const ctxSubdomain = () => import("./steps/contextual/subdomain");
const ctxTagSetup = () => import("./steps/contextual/tag-setup");
const ctxDestinations = () => import("./steps/contextual/destinations");
const ctxEmailSms = () => import("./steps/contextual/email-sms");
const ctxAdPlatforms = () => import("./steps/contextual/ad-platforms");
const ctxCompletion = () => import("./steps/contextual/completion");

const recOverview = () => import("./steps/recover/overview");
const recHowItWorks = () => import("./steps/recover/how-it-works");
const recBusinessInfo = () => import("./steps/recover/business-info");
const recMessaging = () => import("./steps/recover/messaging");
const recBilling = () => import("./steps/recover/billing");
const recGoLive = () => import("./steps/recover/go-live");
const recCompletion = () => import("./steps/recover/completion");

const retOverview = () => import("./steps/returns/overview");
const retReturnPolicy = () => import("./steps/returns/return-policy");
const retReturnOptions = () => import("./steps/returns/return-options");
const retReturnReasons = () => import("./steps/returns/return-reasons");
const retReturnAddress = () => import("./steps/returns/return-address");
const retPortalPreview = () => import("./steps/returns/portal-preview");
const retAboutCoverage = () => import("./steps/returns/about-coverage");
const retCoverageWidget = () => import("./steps/returns/coverage-widget");
const retAppEmbed = () => import("./steps/returns/app-embed");
const retCommunications = () => import("./steps/returns/communications");
const retBilling = () => import("./steps/returns/billing");
const retGoLive = () => import("./steps/returns/go-live");
const retCompletion = () => import("./steps/returns/completion");
const retScheduleCall = () => import("./steps/returns/schedule-call");

export const STEP_COMPONENTS: Record<
  string,
  React.LazyExoticComponent<React.ComponentType>
> = {
  "schedule-call": lazy(() => import("./steps/schedule-call")),
  "cb-overview": lazy(cbOverview),
  "cb-how-it-works": lazy(cbHowItWorks),
  "cb-permissions": lazy(cbPermissions),
  "cb-dispute-sources": lazy(cbDisputeSources),
  "cb-billing": lazy(cbBilling),
  "cb-go-live": lazy(cbGoLive),
  "cb-completion": lazy(cbCompletion),
  "ctx-overview": lazy(ctxOverview),
  "ctx-subdomain": lazy(ctxSubdomain),
  "ctx-tag-setup": lazy(ctxTagSetup),
  "ctx-destinations": lazy(ctxDestinations),
  "ctx-email-sms": lazy(ctxEmailSms),
  "ctx-ad-platforms": lazy(ctxAdPlatforms),
  "ctx-completion": lazy(ctxCompletion),
  "sf-overview": lazy(sfOverview),
  "sf-current-software": lazy(sfCurrentSoftware),
  "sf-channels": lazy(sfChannels),
  "sf-carriers": lazy(sfCarriers),
  "sf-packaging": lazy(sfPackaging),
  "sf-packing-slip": lazy(sfPackingSlip),
  "sf-shipping-automations": lazy(sfShippingAutomations),
  "sf-order-automations": lazy(sfOrderAutomations),
  "sf-international": lazy(sfInternational),
  "sf-views": lazy(sfViews),
  "sf-billing": lazy(sfBilling),
  "sf-pain-points": lazy(sfPainPoints),
  "sf-completion": lazy(sfCompletion),
  "oe-overview": lazy(oeOverview),
  "oe-how-it-works": lazy(oeVideoIntro),
  "oe-customer-actions": lazy(oeEnabledActions),
  "oe-timing": lazy(oeTimeWindow),
  "oe-platforms": lazy(oePlatforms),
  "oe-widget-preview": lazy(oeWidgetPreview),
  "oe-activate": lazy(oeAppEmbed),
  "oe-completion": lazy(oeCompletion),
  "rec-overview": lazy(recOverview),
  "rec-how-it-works": lazy(recHowItWorks),
  "rec-business-info": lazy(recBusinessInfo),
  "rec-messaging": lazy(recMessaging),
  "rec-billing": lazy(recBilling),
  "rec-go-live": lazy(recGoLive),
  "rec-completion": lazy(recCompletion),
  "ret-overview": lazy(retOverview),
  "ret-return-policy": lazy(retReturnPolicy),
  "ret-return-options": lazy(retReturnOptions),
  "ret-return-reasons": lazy(retReturnReasons),
  "ret-return-address": lazy(retReturnAddress),
  "ret-portal-preview": lazy(retPortalPreview),
  "ret-about-coverage": lazy(retAboutCoverage),
  "ret-coverage-widget": lazy(retCoverageWidget),
  "ret-app-embed": lazy(retAppEmbed),
  "ret-communications": lazy(retCommunications),
  "ret-billing": lazy(retBilling),
  "ret-go-live": lazy(retGoLive),
  "ret-completion": lazy(retCompletion),
  "ret-schedule-call": lazy(retScheduleCall),
};

export const STEP_PREFETCH: Record<string, () => void> = {
  "cb-overview": cbOverview,
  "cb-how-it-works": cbHowItWorks,
  "cb-permissions": cbPermissions,
  "cb-dispute-sources": cbDisputeSources,
  "cb-billing": cbBilling,
  "cb-go-live": cbGoLive,
  "cb-completion": cbCompletion,
  "ctx-overview": ctxOverview,
  "ctx-subdomain": ctxSubdomain,
  "ctx-tag-setup": ctxTagSetup,
  "ctx-destinations": ctxDestinations,
  "ctx-email-sms": ctxEmailSms,
  "ctx-ad-platforms": ctxAdPlatforms,
  "ctx-completion": ctxCompletion,
  "sf-overview": sfOverview,
  "sf-current-software": sfCurrentSoftware,
  "sf-channels": sfChannels,
  "sf-carriers": sfCarriers,
  "sf-packaging": sfPackaging,
  "sf-packing-slip": sfPackingSlip,
  "sf-shipping-automations": sfShippingAutomations,
  "sf-order-automations": sfOrderAutomations,
  "sf-international": sfInternational,
  "sf-views": sfViews,
  "sf-billing": sfBilling,
  "sf-pain-points": sfPainPoints,
  "sf-completion": sfCompletion,
  "oe-overview": oeOverview,
  "oe-how-it-works": oeVideoIntro,
  "oe-customer-actions": oeEnabledActions,
  "oe-timing": oeTimeWindow,
  "oe-platforms": oePlatforms,
  "oe-widget-preview": oeWidgetPreview,
  "oe-activate": oeAppEmbed,
  "oe-completion": oeCompletion,
  "rec-overview": recOverview,
  "rec-how-it-works": recHowItWorks,
  "rec-business-info": recBusinessInfo,
  "rec-messaging": recMessaging,
  "rec-billing": recBilling,
  "rec-go-live": recGoLive,
  "rec-completion": recCompletion,
  "ret-overview": retOverview,
  "ret-return-policy": retReturnPolicy,
  "ret-return-options": retReturnOptions,
  "ret-return-reasons": retReturnReasons,
  "ret-return-address": retReturnAddress,
  "ret-portal-preview": retPortalPreview,
  "ret-about-coverage": retAboutCoverage,
  "ret-coverage-widget": retCoverageWidget,
  "ret-app-embed": retAppEmbed,
  "ret-communications": retCommunications,
  "ret-billing": retBilling,
  "ret-go-live": retGoLive,
  "ret-completion": retCompletion,
  "ret-schedule-call": retScheduleCall,
};

if (process.env.NODE_ENV !== "production") {
  const missing = PRODUCTS.flatMap((p) =>
    p.steps.filter((s) => !STEP_COMPONENTS[s.id]).map((s) => `${p.id}/${s.id}`),
  );
  if (missing.length > 0) {
    console.error(
      `[step-components] Missing STEP_COMPONENTS entries: ${missing.join(", ")}`,
    );
  }
}
